import { ClassicRenderer } from './classic/ClassicRenderer';
import type { GameRenderer,RenderSnapshot,RenderFrame } from './RendererInterface';
import type { GameEvent } from '../game/state';
import type { GraphicsMode,GraphicsSettings } from '../settings/GraphicsSettings';
export type ModernFactory=(parent:HTMLElement,settings:GraphicsSettings,failed:(reason:string)=>void)=>Promise<GameRenderer>;
const loadModern:ModernFactory=async(parent,settings,failed)=>{
  const [{ModernRenderer},{preloadMaterials}]=await Promise.all([import('./modern/ModernRenderer'),import('./modern/MaterialLibrary')]);
  await preloadMaterials();return new ModernRenderer(parent,settings,failed);
};
export class RendererManager {
  private classic:GameRenderer;private modern:GameRenderer|null=null;private active:GameRenderer;
  private snapshot:RenderSnapshot|null=null;private frame:RenderFrame={title:true,wallTime:0,reducedMotion:false};
  private disposed=false;private switching=false;
  constructor(readonly canvas:HTMLCanvasElement,private settings:GraphicsSettings,private notice:(message:string)=>void,private fallback:()=>void,private factory:ModernFactory=loadModern){
    this.classic=new ClassicRenderer(canvas);this.active=this.classic;
    this.present();
  }
  get mode():GraphicsMode{return this.active.mode;}
  get busy():boolean{return this.switching;}
  async setMode(mode:GraphicsMode):Promise<GraphicsMode>{
    if(this.disposed||this.switching)return this.mode;
    if(mode===this.mode)return this.mode;
    this.switching=true;
    try{
      if(mode==='modern'){
        if(!this.modern)this.modern=await this.factory(this.canvas.parentElement!,this.settings,this.contextFailure);
        if(this.disposed){this.modern.dispose();this.modern=null;return 'classic';}
        this.modern.configure(this.settings);this.modern.resume();
        // Draw the retained snapshot successfully before making Classic invisible.
        if(this.snapshot)this.modern.draw(this.snapshot,this.frame);
        this.active.suspend();this.active=this.modern;
      }else{this.active.suspend();this.active=this.classic;this.classic.resume();if(this.snapshot)this.classic.draw(this.snapshot,this.frame);}
      this.present();
    }catch(error){
      this.modern?.dispose();this.modern=null;this.active=this.classic;this.classic.resume();this.present();
      this.notice(`${error instanceof Error?error.message:'3D konnte nicht gestartet werden.'} Classic Arcade bleibt aktiv.`);
    }finally{this.switching=false;}
    return this.mode;
  }
  private contextFailure=(message:string):void=>{
    if(this.disposed)return;
    this.active=this.classic;this.classic.resume();this.present();if(this.snapshot)this.classic.draw(this.snapshot,this.frame);
    const damaged=this.modern;this.modern=null;
    // Let Three finish processing the context event before releasing the scene.
    queueMicrotask(()=>damaged?.dispose());this.notice(message);this.fallback();
  };
  private present():void{
    const modern=this.mode==='modern';this.canvas.parentElement!.classList.toggle('is-modern',modern);
    document.body.dataset.graphicsMode=this.mode;this.canvas.dataset.graphicsMode=this.mode;
  }
  draw(snapshot:RenderSnapshot,frame:RenderFrame):void{
    this.snapshot=snapshot;this.frame=frame;
    try{this.active.draw(snapshot,frame);}catch{this.contextFailure('3D-Grafik konnte nicht fortgesetzt werden. Classic Arcade übernimmt.');}
  }
  events(events:readonly GameEvent[],snapshot:RenderSnapshot):void{this.active.events(events,snapshot);}
  configure(settings:GraphicsSettings):void{this.settings={...settings};this.classic.configure(settings);this.modern?.configure(settings);}
  suspend():void{this.active.suspend();}
  resume():void{this.active.resume();}
  dispose():void{if(this.disposed)return;this.disposed=true;this.modern?.dispose();this.modern=null;this.classic.dispose();}
}
